import { useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Conversation, UserSummary } from "@atrium/shared";
import { errorMessage } from "../../lib/api";
import { isRecoveryCodeShaped, safetyNumber, WrongPasswordError, WrongRecoveryCodeError } from "../../lib/e2ee/crypto";
import {
  acceptKey,
  changeRecoveryCode,
  enablePasswordUnlock,
  myFingerprint,
  newRecoveryCode,
  publicKeys,
  reset,
  setUp,
  setUpQuietly,
  setVerified,
  snapshot,
  subscribe,
  trustOf,
  unlock,
  unlockWithPassword,
  type Trust,
} from "../../lib/e2ee/session";
import { Button, IconButton } from "../../components/ui/Button";
import { Dialog, confirm } from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import { SettingsGroup, SettingsRow } from "../../components/ui/Settings";
import { toast } from "../../components/ui/Toast";
import { Avatar } from "../../components/ui/Avatar";
import { IconCopy, IconLock, IconX } from "../../components/icons";

export const useE2ee = () => useSyncExternalStore(subscribe, snapshot);

/** After keys load or change on this device, re-run decryption of everything on screen. */
export function useRedecryptOnUnlock() {
  const qc = useQueryClient();
  const { status } = useE2ee();
  useEffect(() => {
    if (status === "loading") return;
    void qc.invalidateQueries({ queryKey: ["dm"] });
    void qc.invalidateQueries({ queryKey: ["conversations"] });
    void qc.invalidateQueries({ queryKey: ["conversation"] });
  }, [status, qc]);
}

const groupFingerprint = (fp: string) => fp.slice(0, 32).match(/.{4}/g)!.join(" ");

/* ───── Recovery code ───── */

function RecoveryCodeDialog({ code, onDone }: { code: string | null; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  useEffect(() => setSaved(false), [code]);
  const copy = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code).catch(() => {});
    toast.success("Recovery code copied");
  };
  const download = () => {
    if (!code) return;
    const blob = new Blob([`Atrium recovery code for encrypted messages\n\n${code}\n\nKeep this somewhere safe. Anyone with it and your account can read your encrypted messages.\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "atrium-recovery-code.txt";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <Dialog
      open={!!code}
      onOpenChange={(o) => !o && saved && onDone()}
      title="Save your recovery code"
      description="You'll need it to read encrypted messages on another device or after signing out. We can't recover it for you: we never see it."
      size="sm"
      footer={
        <Button variant="primary" disabled={!saved} onClick={onDone}>
          Done
        </Button>
      }
    >
      <div className="rounded-lg border border-line bg-surface px-3 py-3 text-center font-mono text-base tracking-wider select-all" aria-label="Recovery code">
        {code}
      </div>
      <div className="mt-3 flex gap-2">
        <Button icon={IconCopy} onClick={() => void copy()}>
          Copy
        </Button>
        <Button onClick={download}>Download</Button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-fg-2">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        I've saved my recovery code somewhere safe
      </label>
    </Dialog>
  );
}

function CodeInput({ value, onChange, onEnter, label }: { value: string; onChange: (v: string) => void; onEnter: () => void; label: string }) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && onEnter()}
      placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
      aria-label={label}
      autoComplete="off"
      spellCheck={false}
      className="font-mono"
    />
  );
}

/** Asks for the account password before publishing keys (setup or reset). */
function PasswordDialog({ open, onOpenChange, title, description, confirmLabel, danger, onSubmit }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description: string; confirmLabel: string; danger?: boolean; onSubmit: (password: string) => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!open) setPassword("");
  }, [open]);
  const submit = async () => {
    if (!password) return;
    setPending(true);
    try {
      await onSubmit(password);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <Button variant={danger ? "danger" : "primary"} loading={pending} disabled={!password} onClick={() => void submit()}>
          {confirmLabel}
        </Button>
      }
    >
      <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} placeholder="Password" aria-label="Your password" autoComplete="current-password" />
    </Dialog>
  );
}

export function UnlockDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { server } = useE2ee();
  const [mode, setMode] = useState<"password" | "code">("code");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [newCode, setNewCode] = useState<string | null>(null);
  useEffect(() => {
    if (open) setMode(server?.passwordBackup ? "password" : "code");
    else {
      setCode("");
      setPassword("");
    }
  }, [open, server?.passwordBackup]);
  const submit = async () => {
    if (mode === "code" && !isRecoveryCodeShaped(code)) return toast.error("Recovery codes are 32 letters and numbers");
    if (mode === "password" && !password) return;
    setPending(true);
    try {
      await (mode === "code" ? unlock(code) : unlockWithPassword(password));
      toast.success("Encrypted messages unlocked on this device");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof WrongRecoveryCodeError || e instanceof WrongPasswordError ? e.message : errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  const [resetting, setResetting] = useState(false);
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title="Unlock encrypted messages"
        description={
          mode === "password"
            ? "Enter your account password to read and send encrypted messages on this device."
            : "Enter the recovery code you saved when you set up encrypted messages."
        }
        size="sm"
        footer={
          <>
            <Button variant="ghost" className="mr-auto" onClick={() => setResetting(true)}>
              Lost it? Reset
            </Button>
            <Button variant="primary" loading={pending} onClick={() => void submit()}>
              Unlock
            </Button>
          </>
        }
      >
        {mode === "code" ? (
          <CodeInput value={code} onChange={setCode} onEnter={() => void submit()} label="Recovery code" />
        ) : (
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} placeholder="Password" aria-label="Your password" autoComplete="current-password" />
        )}
        {server?.passwordBackup && (
          <button type="button" className="mt-3 text-sm text-fg-2 hover:text-fg hover:underline" onClick={() => setMode(mode === "code" ? "password" : "code")}>
            {mode === "code" ? "Use your password instead" : "Use your recovery code instead"}
          </button>
        )}
      </Dialog>
      <PasswordDialog
        open={resetting}
        onOpenChange={setResetting}
        title="Reset encryption keys?"
        description="You'll get new keys and a new recovery code. Encrypted messages you've received so far can't be read anymore, on any device, and people you talk to will see that your key changed. Enter your password to continue."
        confirmLabel="Reset keys"
        danger
        onSubmit={async (pw) => {
          setNewCode(await reset(pw));
          onOpenChange(false);
        }}
      />
      <RecoveryCodeDialog code={newCode} onDone={() => setNewCode(null)} />
    </>
  );
}

/** For accounts that predate encryption by default (or whose automatic setup failed): create keys with the password. */
export function SetUpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <PasswordDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Turn on encrypted messages"
      description="Your messages will be end-to-end encrypted: only the people in a conversation can read them, on their devices. Enter your password to create your keys. Signing in with it unlocks them on other devices."
      confirmLabel="Turn on"
      onSubmit={async (pw) => {
        await setUpQuietly(pw);
        toast.success("Encrypted messages are on");
      }}
    />
  );
}

/**
 * Keys made automatically at sign-in have a recovery code nobody has seen.
 * Without one, forgetting the password means losing encrypted history, so
 * nudge (dismissibly) until one's saved.
 */
export function RecoveryCodeNudge() {
  const { server, userId } = useE2ee();
  const storageKey = `atrium:e2ee-nudge-dismissed:${userId}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === "1");
  const [asking, setAsking] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  if (!server || server.recoveryConfirmed || (dismissed && !code)) return <RecoveryCodeDialog code={code} onDone={() => setCode(null)} />;
  return (
    <>
      <div className="mx-3 mb-2 flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-2">
        <IconLock className="icon size-4 shrink-0" aria-hidden />
        <span className="flex-1">Save a recovery code, so you can still read your encrypted messages if you forget your password.</span>
        <Button size="sm" variant="primary" onClick={() => setAsking(true)}>
          Create code
        </Button>
        <IconButton
          label="Not now"
          icon={IconX}
          size="sm"
          onClick={() => {
            localStorage.setItem(storageKey, "1");
            setDismissed(true);
          }}
        />
      </div>
      <PasswordDialog
        open={asking}
        onOpenChange={setAsking}
        title="Create a recovery code"
        description="Enter your password. You'll get a code to keep somewhere safe."
        confirmLabel="Continue"
        onSubmit={async (pw) => setCode(await newRecoveryCode(pw))}
      />
      <RecoveryCodeDialog code={code} onDone={() => setCode(null)} />
    </>
  );
}

/** Settings → Privacy: set up, unlock, change the recovery code, reset. */
export function E2eeSettings() {
  const { status, server } = useE2ee();
  const [code, setCode] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [changing, setChanging] = useState(false);
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  /** Errors propagate so the password dialog stays open on a wrong password. */
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      setCode(await fn());
    } finally {
      setBusy(false);
    }
  };
  const [asking, setAsking] = useState<"setup" | "reset" | "recovery" | null>(null);
  const [enabling, setEnabling] = useState(false);
  const enable = async () => {
    if (!isRecoveryCodeShaped(current)) return toast.error("Enter your recovery code");
    if (!password) return toast.error("Enter your password");
    setBusy(true);
    try {
      await enablePasswordUnlock(current, password);
      toast.success("Signing in now unlocks your encrypted messages");
      setEnabling(false);
      setCurrent("");
      setPassword("");
    } catch (e) {
      toast.error(e instanceof WrongRecoveryCodeError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const change = async () => {
    if (!isRecoveryCodeShaped(current)) return toast.error("Enter your current recovery code");
    if (!password) return toast.error("Enter your password");
    setBusy(true);
    try {
      setCode(await changeRecoveryCode(current, password));
      setChanging(false);
      setCurrent("");
      setPassword("");
    } catch (e) {
      toast.error(e instanceof WrongRecoveryCodeError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsGroup
      title="End-to-end encrypted messages"
      description="Encrypted conversations can only be read on your devices. Atrium's servers can't read them, so they aren't searchable and can't be recovered without your recovery code."
    >
      {status === "unavailable" && <SettingsRow title="Not available" description="This browser doesn't support the storage or cryptography encrypted messages need." icon={IconLock}>{null}</SettingsRow>}
      {status === "loading" && <SettingsRow title="Loading…" icon={IconLock}>{null}</SettingsRow>}
      {status === "off" && (
        <SettingsRow title="Set up encrypted messages" description="Creates your keys on this device and a recovery code for your other devices." icon={IconLock}>
          <Button variant="primary" loading={busy} onClick={() => setAsking("setup")}>
            Set up
          </Button>
        </SettingsRow>
      )}
      {status === "locked" && (
        <SettingsRow title="Locked on this device" description="Enter your recovery code to read and send encrypted messages here." icon={IconLock}>
          <Button variant="primary" onClick={() => setUnlocking(true)}>
            Unlock
          </Button>
        </SettingsRow>
      )}
      {status === "ready" && server && (
        <>
          <SettingsRow title="Your security key" description={<span className="font-mono">{groupFingerprint(server.fingerprint)}</span>} icon={IconLock}>
            {null}
          </SettingsRow>
          {server.recoveryConfirmed ? (
            <SettingsRow title="Recovery code" description="Replace it with a new one. Needs your current code and your password.">
              <Button onClick={() => setChanging(true)}>Change</Button>
            </SettingsRow>
          ) : (
            <SettingsRow title="Recovery code" description="You haven't saved one yet. Without it, forgetting your password means losing your encrypted messages.">
              <Button variant="primary" onClick={() => setAsking("recovery")}>
                Create
              </Button>
            </SettingsRow>
          )}
          {!server.passwordBackup && (
            <SettingsRow title="Unlock when you sign in" description="Let your password unlock encrypted messages on new devices, so you only need the recovery code if you forget it.">
              <Button onClick={() => setEnabling(true)}>Turn on</Button>
            </SettingsRow>
          )}
          <SettingsRow title="Reset keys" description="Only if your recovery code is lost or exposed. Past encrypted messages become unreadable.">
            <Button variant="danger" loading={busy} onClick={() => setAsking("reset")}>
              Reset
            </Button>
          </SettingsRow>
        </>
      )}
      <UnlockDialog open={unlocking} onOpenChange={setUnlocking} />
      <Dialog
        open={enabling}
        onOpenChange={(o) => {
          setEnabling(o);
          if (!o) setPassword("");
        }}
        title="Unlock when you sign in"
        description="Your recovery code opens your keys once, so they can be sealed under your password too."
        size="sm"
        footer={
          <Button variant="primary" loading={busy} onClick={() => void enable()}>
            Turn on
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <CodeInput value={current} onChange={setCurrent} onEnter={() => void enable()} label="Recovery code" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void enable()} placeholder="Password" aria-label="Your password" autoComplete="current-password" />
        </div>
      </Dialog>
      <Dialog
        open={changing}
        onOpenChange={(o) => {
          setChanging(o);
          if (!o) setPassword("");
        }}
        title="Change recovery code"
        size="sm"
        footer={
          <Button variant="primary" loading={busy} onClick={() => void change()}>
            Continue
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <CodeInput value={current} onChange={setCurrent} onEnter={() => void change()} label="Current recovery code" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void change()} placeholder="Password" aria-label="Your password" autoComplete="current-password" />
        </div>
      </Dialog>
      <PasswordDialog
        open={asking !== null}
        onOpenChange={(o) => !o && setAsking(null)}
        title={asking === "reset" ? "Reset encryption keys?" : asking === "recovery" ? "Create a recovery code" : "Set up encrypted messages"}
        description={
          asking === "recovery"
            ? "Enter your password. You'll get a code to keep somewhere safe."
            : asking === "reset"
            ? "Encrypted messages you've received so far can't be read anymore, on any device, and people you talk to will see that your key changed. Enter your password to continue."
            : "Enter your password to create your keys. You'll get a recovery code next."
        }
        confirmLabel={asking === "reset" ? "Reset keys" : "Continue"}
        danger={asking === "reset"}
        onSubmit={(pw) => run(() => (asking === "reset" ? reset(pw) : asking === "recovery" ? newRecoveryCode(pw) : setUp(pw)))}
      />
      <RecoveryCodeDialog code={code} onDone={() => setCode(null)} />
    </SettingsGroup>
  );
}

/* ───── Conversation: trust and verification ───── */

type MemberTrust = { user: UserSummary; trust: Trust; safety: string | null };

/** Trust state for every other member; re-checks when keys or pins change. */
export function useMemberTrust(conv: Conversation | undefined, meId: string) {
  const { status } = useE2ee();
  const [rows, setRows] = useState<MemberTrust[]>([]);
  const [tick, setTick] = useState(0);
  const memberKey = conv?.members.map((m) => m.id).join(",");
  useEffect(() => {
    if (!conv?.encryption.enabled || status !== "ready") return setRows([]);
    let live = true;
    void (async () => {
      const others = conv.members.filter((m) => m.id !== meId);
      const keys = await publicKeys(others.map((m) => m.id), true).catch(() => new Map());
      const mine = await myFingerprint();
      const out: MemberTrust[] = [];
      for (const u of others) {
        const k = keys.get(u.id);
        out.push({ user: u, trust: await trustOf(u.id, k), safety: k && mine ? await safetyNumber(mine, k.computed) : null });
      }
      if (live) setRows(out);
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id, conv?.encryption.enabled, conv?.encryption.keyVersion, memberKey, status, meId, tick]);
  return { rows, refresh: () => setTick((t) => t + 1) };
}

export function VerifyDialog({ open, onOpenChange, rows, onChanged }: { open: boolean; onOpenChange: (o: boolean) => void; rows: MemberTrust[]; onChanged: () => void }) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Encryption and security"
      description="Messages here are end-to-end encrypted. Compare safety numbers in person or over another app: if they match on both screens, nobody is in the middle."
    >
      <ul className="flex flex-col gap-4">
        {rows.map((r) => (
          <li key={r.user.id} className="rounded-lg border border-line p-3">
            <div className="flex items-center gap-2">
              <Avatar src={r.user.avatarUrl} name={r.user.displayName} size="sm" />
              <span className="font-medium">{r.user.displayName}</span>
              <span className={r.trust === "changed" ? "ml-auto text-xs font-medium text-danger" : "ml-auto text-xs text-fg-3"}>
                {r.trust === "verified" ? "Verified" : r.trust === "changed" ? "Key changed" : r.trust === "new" ? "No key" : "Not verified"}
              </span>
            </div>
            {r.safety && <div className="mt-2 font-mono text-sm tracking-wide text-fg-2">{r.safety}</div>}
            <div className="mt-2 flex gap-2">
              {r.trust === "changed" && (
                <Button
                  size="sm"
                  onClick={() =>
                    void acceptKey(r.user.id)
                      .then(onChanged)
                      .catch((e) => toast.error(errorMessage(e)))
                  }
                >
                  Accept new key
                </Button>
              )}
              {(r.trust === "trusted" || r.trust === "changed") && r.safety && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() =>
                    void (r.trust === "changed" ? acceptKey(r.user.id, true) : setVerified(r.user.id, true))
                      .then(onChanged)
                      .catch((e) => toast.error(errorMessage(e)))
                  }
                >
                  They match, mark verified
                </Button>
              )}
              {r.trust === "verified" && (
                <Button size="sm" variant="ghost" onClick={() => void setVerified(r.user.id, false).then(onChanged)}>
                  Unverify
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** Mounted once in the app shell: re-decrypts what's cached when this device's keys load or change. */
export function E2eeRedecrypt() {
  useRedecryptOnUnlock();
  return null;
}
